import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { reveal, staggerParent } from './variants';

/**
 * Scroll-triggered fade+rise. Fires once when ~80px into the viewport.
 * Under prefers-reduced-motion it renders the final state immediately.
 *
 * @param {object} props
 * @param {keyof JSX.IntrinsicElements} [props.as='div']
 * @param {boolean} [props.stagger=false]  stagger direct children (each child should be a <Reveal.Item>)
 * @param {number}  [props.delay=0]
 */
export function Reveal({ as = 'div', stagger = false, delay = 0, className, children, ...rest }) {
    const reduce = useReducedMotion();
    const MotionTag = motion[as] || motion.div;

    if (reduce) {
        const Tag = as;
        return <Tag className={className} {...rest}>{children}</Tag>;
    }

    return (
        <MotionTag
            className={className}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger ? staggerParent : reveal}
            transition={delay ? { delay } : undefined}
            {...rest}
        >
            {children}
        </MotionTag>
    );
}

/** A single staggered child; use inside <Reveal stagger>. */
Reveal.Item = function RevealItem({ as = 'div', className, children, ...rest }) {
    const reduce = useReducedMotion();
    const MotionTag = motion[as] || motion.div;
    if (reduce) {
        const Tag = as;
        return <Tag className={className} {...rest}>{children}</Tag>;
    }
    return (
        <MotionTag className={className} variants={reveal} {...rest}>
            {children}
        </MotionTag>
    );
};

export default Reveal;
